// src/components/timeline/TimeDropdown.tsx
import React, { useRef, useState, useEffect } from 'react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'

import { FiChevronDown, FiChevronUp } from 'react-icons/fi'
import { Button } from '../../styles/_app.style'
import { Item } from '../../styles/navigation.style'
import { Content } from '../navigation/navDropdowns/navDropdown.style'

interface TimeDropdownProps {
  value: string
  options: string[]
  onChange: (v: string) => void
  label?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  triggerDataAttr?: string
}

const TimeDropdown: React.FC<TimeDropdownProps> = ({
  value,
  options,
  onChange,
  open: parentOpen, // Rename to avoid conflict
  onOpenChange: parentOnOpenChange, // Rename to avoid conflict
  label = 'Select time',
  triggerDataAttr = 'slot',
}) => {
  const [typedValue, setTypedValue] = useState<string>(value)
  const inputRef = useRef<HTMLInputElement>(null)
  
  // --- Start of Fix ---

  // 1. Add local state to manage the open state internally.
  const [internalOpen, setInternalOpen] = useState(false)

  // 2. Determine if the component is controlled by its parent.
  const isControlled = parentOpen !== undefined && parentOnOpenChange !== undefined

  // 3. Use the parent's state if controlled, otherwise use internal state.
  const open = isControlled ? parentOpen : internalOpen
  
  // 4. Use the parent's handler if controlled, otherwise update internal state.
  const onOpenChange = (newOpenState: boolean) => {
    if (isControlled) {
      parentOnOpenChange(newOpenState)
    } else {
      setInternalOpen(newOpenState)
    }
  }

  // --- End of Fix ---

  useEffect(() => {
    setTypedValue(value)
  }, [value])

  const handleInputBlur = () => {
    const isValidOption = options.includes(typedValue)
    const isValidFormat = /^\d{2}:\d{2}$/.test(typedValue)
    if (isValidOption || isValidFormat) {
      onChange(typedValue)
    } else {
      setTypedValue(value)
    }
  }

  return (
    // Use the derived 'open' and 'onOpenChange' values here
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '$md'}}>
        <input
          ref={inputRef}
          type="text"
          placeholder="HH:MM"
          value={typedValue}
          onChange={(e) => setTypedValue(e.target.value)}
          onBlur={handleInputBlur}
          style={{
            padding: '0.25rem 0.5rem',
            border: '1px solid var(--separator)',
            borderRadius: 3,
            backgroundColor: 'white',
            // fontSize: 'var(--font-size-sm)', // Use CSS variable for consistency
            // lineHeight: '1.5',
            fontSize: 'inherit',
            width: '4rem',

          }}
        />

        <DropdownMenuTrigger
          asChild
          data-dropdown={triggerDataAttr}
          data-radix-dropdown-menu-trigger
        >
          <Button
            icon
            aria-label={label}
            title={label}
            css={{ minWidth: '2rem', padding: '0.25rem', fontSize: '$md',   
              '&:hover': {backgroundColor: 'transparent'}, 
              '&[data-state="open"]': { backgroundColor: 'transparent' },
              '&:active': {backgroundColor: 'transparent'},
            '@media (max-width: 600px)': {
                display: 'none',
              }
            }}
          >
            {open ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
          </Button>
        </DropdownMenuTrigger>
      </div>

      <Content
        align="start"
        style={{
          minWidth: '6rem',
          maxHeight: '40vh',
          overflowY: 'auto',
          fontSize: 'var(--font-size-md)',
        }}
      >
        {options.map((opt) => (
          <Item
            key={opt}
            onSelect={() => {
              onChange(opt)
              setTypedValue(opt)
            }}
            style={{ width: '100%' }}
            css={{fontSize: '$md'}}
            title={opt}
          >
            {opt}
          </Item>
        ))}
      </Content>
    </DropdownMenu>
  )
}

export default TimeDropdown